import Dexie, { type Table } from "dexie";
import type {
  BackupMeta,
  DataProtectionState,
  ExpenseRecord,
  PaymentRecord,
  Trip,
  TripFilterPreset,
  TripSavePayload,
} from "./types";
import {
  hydrateExpenseRecord,
  hydratePaymentRecord,
  hydrateTrip,
  hydrateTripWithRecords,
} from "./rules";
import { generateId } from "../shared/id";

export const DATABASE_NAME = "transport_erp_local";
const STORAGE_KEY = "transport_erp_pro_v5";
const LEGACY_STORAGE_KEY = "transport_erp_pro_v4";
const MIGRATION_KEY = "legacy-local-storage-trips";
const LINE_ITEM_MIGRATION_KEY = "trip-line-items-v1";
const LAST_BACKUP_KEY = "last-backup-at";
const LAST_DATA_CHANGE_KEY = "last-data-change-at";

type LocalMeta = {
  key: string;
  value: string;
  updatedAt: string;
};

export class TransportErpDatabase extends Dexie {
  trips!: Table<Trip, string>;
  payments!: Table<PaymentRecord, string>;
  expenses!: Table<ExpenseRecord, string>;
  filterPresets!: Table<TripFilterPreset, string>;
  meta!: Table<LocalMeta, string>;

  constructor(databaseName = DATABASE_NAME) {
    super(databaseName);
    this.version(1).stores({
      trips: "id,date,monthKey,vehicleNo,partyName,status",
      meta: "key",
    });
    this.version(2).stores({
      trips:
        "id,tripNo,date,monthKey,vehicleNo,partyName,status,bookingType,vehicleSource,brokerName",
      meta: "key",
    });
    this.version(3).stores({
      trips:
        "id,tripNo,date,monthKey,vehicleNo,partyName,status,bookingType,vehicleSource,brokerName,settledAt",
      payments: "id,tripId,date,mode",
      expenses: "id,tripId,date,category",
      filterPresets: "id,name,updatedAt",
      meta: "key",
    });
  }
}

export const db = new TransportErpDatabase();

const writeMeta = async (key: string, value: string): Promise<void> => {
  await db.meta.put({ key, value, updatedAt: new Date().toISOString() });
};

const markDataChangedInTransaction = async (): Promise<void> => {
  const changedAt = new Date().toISOString();
  await db.meta.put({
    key: LAST_DATA_CHANGE_KEY,
    value: changedAt,
    updatedAt: changedAt,
  });
};

export const loadBackupMeta = async (): Promise<BackupMeta> => {
  const [backup, change] = await Promise.all([
    db.meta.get(LAST_BACKUP_KEY),
    db.meta.get(LAST_DATA_CHANGE_KEY),
  ]);

  return {
    lastBackupAt: backup?.value || undefined,
    lastDataChangeAt: change?.value || undefined,
  };
};

export const markBackupCompleted = async (): Promise<BackupMeta> => {
  await writeMeta(LAST_BACKUP_KEY, new Date().toISOString());
  return loadBackupMeta();
};

export const getDataProtectionState = async (): Promise<DataProtectionState> => {
  if (typeof navigator === "undefined" || !navigator.storage?.persisted) {
    return "unsupported";
  }

  return (await navigator.storage.persisted()) ? "protected" : "unprotected";
};

export const requestPersistentStorage = async (): Promise<DataProtectionState> => {
  if (typeof navigator === "undefined" || !navigator.storage?.persist) {
    return "unsupported";
  }

  return (await navigator.storage.persist()) ? "protected" : "unprotected";
};

export type TripStorageInit = {
  trips: Trip[];
  migratedCount: number;
  lineItemsMigratedCount: number;
};

const sortTrips = (trips: Trip[]): Trip[] =>
  [...trips].sort((a, b) => b.date.localeCompare(a.date));

const isValidTrip = (trip: Trip): boolean => Boolean(trip.id && trip.date);
const isValidPayment = (payment: PaymentRecord): boolean =>
  Boolean(payment.id && payment.tripId && payment.date && payment.amount > 0);
const isValidExpense = (expense: ExpenseRecord): boolean =>
  Boolean(expense.id && expense.tripId && expense.date && expense.amount > 0);

const hydrateUnknownTrip = (trip: unknown): Trip | null => {
  try {
    const hydrated = hydrateTrip(trip as Trip);
    return isValidTrip(hydrated) ? hydrated : null;
  } catch {
    return null;
  }
};

const hydrateUnknownPayment = (payment: unknown): PaymentRecord | null => {
  try {
    const hydrated = hydratePaymentRecord(payment as PaymentRecord);
    return isValidPayment(hydrated) ? hydrated : null;
  } catch {
    return null;
  }
};

const hydrateUnknownExpense = (expense: unknown): ExpenseRecord | null => {
  try {
    const hydrated = hydrateExpenseRecord(expense as ExpenseRecord);
    return isValidExpense(hydrated) ? hydrated : null;
  } catch {
    return null;
  }
};

const parseLegacyTrips = (raw: string | null): unknown[] => {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const readLegacyTrips = (): Trip[] => {
  if (typeof window === "undefined") return [];

  const latestTrips = parseLegacyTrips(window.localStorage.getItem(STORAGE_KEY));
  const olderTrips = parseLegacyTrips(
    window.localStorage.getItem(LEGACY_STORAGE_KEY)
  );
  const byId = new Map<string, Trip>();

  [...olderTrips, ...latestTrips].forEach((trip) => {
    const hydrated = hydrateUnknownTrip(trip);
    if (hydrated) byId.set(hydrated.id, hydrated);
  });

  return sortTrips(Array.from(byId.values()));
};

export const loadTrips = async (): Promise<Trip[]> => {
  const [trips, payments, expenses] = await Promise.all([
    db.trips.toArray(),
    db.payments.toArray(),
    db.expenses.toArray(),
  ]);
  const paymentsByTrip = groupRecordsByTrip(payments.map(hydratePaymentRecord));
  const expensesByTrip = groupRecordsByTrip(expenses.map(hydrateExpenseRecord));

  return sortTrips(
    trips
      .map((trip) =>
        hydrateTripWithRecords(
          trip,
          paymentsByTrip.get(trip.id) ?? [],
          expensesByTrip.get(trip.id) ?? []
        )
      )
      .filter((trip): trip is Trip => Boolean(trip))
  );
};

const groupRecordsByTrip = <T extends { tripId: string }>(records: T[]): Map<string, T[]> => {
  const byTrip = new Map<string, T[]>();
  records.forEach((record) => {
    byTrip.set(record.tripId, [...(byTrip.get(record.tripId) ?? []), record]);
  });
  return byTrip;
};

export const loadTripRecords = async (
  tripId: string
): Promise<{ payments: PaymentRecord[]; expenses: ExpenseRecord[] }> => {
  const [payments, expenses] = await Promise.all([
    db.payments.where("tripId").equals(tripId).toArray(),
    db.expenses.where("tripId").equals(tripId).toArray(),
  ]);

  return {
    payments: payments
      .map(hydrateUnknownPayment)
      .filter((payment): payment is PaymentRecord => Boolean(payment))
      .sort((a, b) => a.date.localeCompare(b.date)),
    expenses: expenses
      .map(hydrateUnknownExpense)
      .filter((expense): expense is ExpenseRecord => Boolean(expense))
      .sort((a, b) => a.date.localeCompare(b.date)),
  };
};

export const loadFullLocalData = async (): Promise<{
  trips: Trip[];
  payments: PaymentRecord[];
  expenses: ExpenseRecord[];
}> => {
  const [trips, payments, expenses] = await Promise.all([
    loadTrips(),
    db.payments.toArray(),
    db.expenses.toArray(),
  ]);

  return {
    trips,
    payments: payments
      .map(hydrateUnknownPayment)
      .filter((payment): payment is PaymentRecord => Boolean(payment)),
    expenses: expenses
      .map(hydrateUnknownExpense)
      .filter((expense): expense is ExpenseRecord => Boolean(expense)),
  };
};

export const migrateLegacyTrips = async (): Promise<number> => {
  const alreadyMigrated = await db.meta.get(MIGRATION_KEY);
  if (alreadyMigrated) return 0;

  const legacyTrips = readLegacyTrips();
  if (legacyTrips.length > 0) {
    await db.trips.bulkPut(legacyTrips);
  }

  await db.meta.put({
    key: MIGRATION_KEY,
    value: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  return legacyTrips.length;
};

export const migrateTripLineItems = async (): Promise<number> => {
  const alreadyMigrated = await db.meta.get(LINE_ITEM_MIGRATION_KEY);
  if (alreadyMigrated) return 0;

  const [rawTrips, existingPayments, existingExpenses] = await Promise.all([
    db.trips.toArray(),
    db.payments.toArray(),
    db.expenses.toArray(),
  ]);
  const paymentTripIds = new Set(existingPayments.map((payment) => payment.tripId));
  const expenseTripIds = new Set(existingExpenses.map((expense) => expense.tripId));
  const payments: PaymentRecord[] = [];
  const expenses: ExpenseRecord[] = [];

  rawTrips.map(hydrateTrip).forEach((trip) => {
    if (!paymentTripIds.has(trip.id) && trip.advanceReceived > 0) {
      payments.push({
        id: `${trip.id}-payment-advance`,
        tripId: trip.id,
        date: trip.date,
        amount: trip.advanceReceived,
        mode: "Cash",
        note: "Migrated advance received",
      });
    }

    if (!expenseTripIds.has(trip.id)) {
      [
        { id: "fuel", category: "Fuel" as const, amount: trip.fuelExp },
        { id: "fastag", category: "FASTag" as const, amount: trip.fastagExp },
        { id: "driver-bata", category: "Driver Bata" as const, amount: trip.driverBata },
        { id: "commission", category: "Commission" as const, amount: trip.commissionExp },
        { id: "other", category: "Other" as const, amount: trip.otherExp },
      ].forEach((expense) => {
        if (expense.amount > 0) {
          expenses.push({
            id: `${trip.id}-expense-${expense.id}`,
            tripId: trip.id,
            date: trip.date,
            category: expense.category,
            amount: expense.amount,
            note: "Migrated trip expense",
          });
        }
      });
    }
  });

  if (payments.length > 0) await db.payments.bulkPut(payments);
  if (expenses.length > 0) await db.expenses.bulkPut(expenses);

  await db.meta.put({
    key: LINE_ITEM_MIGRATION_KEY,
    value: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  return payments.length + expenses.length;
};

export const initializeTripStorage = async (): Promise<TripStorageInit> => {
  const migratedCount = await migrateLegacyTrips();
  const lineItemsMigratedCount = await migrateTripLineItems();
  const trips = await loadTrips();
  const backupMeta = await loadBackupMeta();
  if (trips.length > 0 && !backupMeta.lastDataChangeAt) {
    await writeMeta(LAST_DATA_CHANGE_KEY, new Date().toISOString());
  }
  return { trips, migratedCount, lineItemsMigratedCount };
};

export const saveTrip = async (trip: Trip): Promise<void> => {
  const hydrated = hydrateTrip(trip);
  if (!isValidTrip(hydrated)) {
    throw new Error("Trip must have an ID and date before it can be saved.");
  }
  await db.transaction("rw", db.trips, db.meta, async () => {
    await db.trips.put(hydrated);
    await markDataChangedInTransaction();
  });
};

export const saveTripWithRecords = async ({
  trip,
  payments,
  expenses,
}: TripSavePayload): Promise<void> => {
  const validPayments = payments.map(hydratePaymentRecord).filter(isValidPayment);
  const validExpenses = expenses.map(hydrateExpenseRecord).filter(isValidExpense);
  const hydrated = hydrateTripWithRecords(trip, validPayments, validExpenses);
  if (!isValidTrip(hydrated)) {
    throw new Error("Trip must have an ID and date before it can be saved.");
  }

  await db.transaction("rw", db.trips, db.payments, db.expenses, db.meta, async () => {
    await db.trips.put(hydrated);
    await Promise.all([
      db.payments.where("tripId").equals(hydrated.id).delete(),
      db.expenses.where("tripId").equals(hydrated.id).delete(),
    ]);
    if (validPayments.length > 0) await db.payments.bulkPut(validPayments);
    if (validExpenses.length > 0) await db.expenses.bulkPut(validExpenses);
    await markDataChangedInTransaction();
  });
};

export const bulkPutTrips = async (trips: Trip[]): Promise<number> => {
  const validTrips = trips.filter(isValidTrip).map((trip) => hydrateTrip(trip));
  if (validTrips.length === 0) return 0;
  await db.transaction("rw", db.trips, db.meta, async () => {
    await db.trips.bulkPut(validTrips);
    await markDataChangedInTransaction();
  });
  return validTrips.length;
};

export const bulkPutPayments = async (
  payments: PaymentRecord[]
): Promise<number> => {
  const validPayments = payments.map(hydratePaymentRecord).filter(isValidPayment);
  if (validPayments.length === 0) return 0;
  await db.transaction("rw", db.payments, db.meta, async () => {
    await db.payments.bulkPut(validPayments);
    await markDataChangedInTransaction();
  });
  return validPayments.length;
};

export const bulkPutExpenses = async (
  expenses: ExpenseRecord[]
): Promise<number> => {
  const validExpenses = expenses.map(hydrateExpenseRecord).filter(isValidExpense);
  if (validExpenses.length === 0) return 0;
  await db.transaction("rw", db.expenses, db.meta, async () => {
    await db.expenses.bulkPut(validExpenses);
    await markDataChangedInTransaction();
  });
  return validExpenses.length;
};

export const deleteTripById = async (tripId: string): Promise<void> => {
  await db.transaction("rw", db.trips, db.payments, db.expenses, db.meta, async () => {
    await Promise.all([
      db.trips.delete(tripId),
      db.payments.where("tripId").equals(tripId).delete(),
      db.expenses.where("tripId").equals(tripId).delete(),
    ]);
    await markDataChangedInTransaction();
  });
};

export const loadFilterPresets = async <TFilters = unknown>(): Promise<
  Array<TripFilterPreset<TFilters>>
> =>
  (await db.filterPresets.toArray())
    .map((preset) => preset as TripFilterPreset<TFilters>)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

export const saveFilterPreset = async <TFilters = unknown>(
  name: string,
  filters: TFilters,
  existingId?: string
): Promise<TripFilterPreset<TFilters>> => {
  const preset: TripFilterPreset<TFilters> = {
    id: existingId || generateId(),
    name: name.trim(),
    filters,
    updatedAt: new Date().toISOString(),
  };
  await db.filterPresets.put(preset as TripFilterPreset);
  return preset;
};

export const deleteFilterPreset = async (presetId: string): Promise<void> => {
  await db.filterPresets.delete(presetId);
};
